import { REACTIONS } from '@groupe-trousseau/shared/contracts';
import { nomAffiche } from '../format/designation';
import { Bouton } from './Bouton';

/**
 * **La rangée de réactions d'un message — `CDC-3` F-40 et DD-27.**
 *
 * « **Les réactions existent** : **une par personne et par message**, choisie dans une
 * **liste fermée tenue par le produit**, jamais un texte libre. »
 *
 * **LA LISTE VIENT DU CONTRAT, ET C'EST CE QUE « TENUE PAR LE PRODUIT » VEUT DIRE.** Elle
 * n'est pas recopiée ici : `REACTIONS` est la constante que `shared/contracts` déclare et
 * que le schéma d'entrée de la route valide. « Un ajout est un changement de contrat,
 * jamais une migration » — et il ne doit pas non plus être un changement de composant.
 * Le même motif que `palette.ts` pour les encres du tableau : une liste fermée vit à un
 * seul endroit, et la surface la lit.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **AUCUN COMPTE, ET C'EST UNE RÈGLE, PAS UN MANQUE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * « Aucun indicateur pour le chat, **zéro sur quatre** » (Q479) : « ce domaine a beaucoup
 * à compter et **personne n'a le droit de le lire** ». Cette rangée n'affiche donc **ni
 * un nombre de personnes, ni un total** — elle montre **ce que la réponse porte** et rien
 * d'autre : la valeur, le prénom de qui l'a posée, et si c'est la vôtre. Un compteur
 * serait un agrégat calculé sur autrui, fabriqué par le front là où le serveur refuse
 * d'en rendre un.
 *
 * **Le geste REMPLACE, et reposer la sienne la retire.** « Une seule par personne et par
 * message : celle-ci remplace la précédente. VIDE la retire. » Il n'y a donc pas de second
 * bouton pour défaire : le même bouton porte les deux sens, et l'état courant se lit sur
 * `aria-pressed`.
 *
 * **Aucune cible en dessous de 44 points** : les six choix passent par `Bouton`, qui tient
 * `min-h-cible` jusque dans sa petite taille. Une rangée d'émojis posés à même le texte
 * serait la façon la plus courante de perdre ce plancher.
 */
type Reaction = (typeof REACTIONS)[number];

/** Ce que le fil rend d'une réaction, et rien de plus. */
type ReactionPosee = {
  readonly valeur: string;
  readonly de: { readonly prenom: string | null };
  readonly deMoi: boolean;
};

type Props = {
  readonly reactions: readonly ReactionPosee[];
  /** Vide retire la sienne. Le même geste remplace et défait. */
  readonly onPoser: (valeur: Reaction | null) => void;
  /** Bloque les six choix le temps de l'aller-retour : un geste qui part ne se double pas. */
  readonly enCours?: boolean;
};

/**
 * **Le nom de chaque réaction, pour qui n'en voit pas le dessin.**
 *
 * Un émoji seul n'a aucun nom accessible stable : le lecteur d'écran lit ce que sa table
 * Unicode lui donne, qui n'est ni du français ni le mot du produit. Ces six libellés sont
 * **de l'étiquetage d'interface, pas du contenu de produit** — le corpus n'en nomme aucun.
 *
 * **Le type `Record<Reaction, string>` est ce qui les tient collés à la liste fermée** :
 * ajouter une réaction au contrat sans la nommer ici ne compile pas.
 */
const NOM_DE_LA_REACTION: Record<Reaction, string> = {
  '👍': 'Pouce levé',
  '❤️': 'Cœur',
  '😂': 'Rire',
  '😮': 'Surprise',
  '😢': 'Tristesse',
  '🙏': 'Merci',
};

/**
 * **La réponse type `valeur` en chaîne libre, la liste est fermée : le repli est là pour
 * cet écart-là.** Le serveur ne peut rendre qu'une valeur de la liste ; si le contrat
 * s'allonge un jour avant ce fichier, une réaction sans nom vaut mieux qu'un nom vide.
 */
function nomLisible(valeur: string): string {
  const noms: Record<string, string | undefined> = NOM_DE_LA_REACTION;
  return noms[valeur] ?? 'Réaction';
}

export function RangeeDeReactions({ reactions, onPoser, enCours = false }: Props) {
  const mienne = reactions.find((posee) => posee.deMoi)?.valeur ?? null;
  // **Les siennes ne se répètent pas** : `aria-pressed` les dit déjà, et les redire en
  // clair ferait lire deux fois la même chose.
  const desAutres = reactions.filter((posee) => !posee.deMoi);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div
        role="group"
        aria-label="Réagir à ce message"
        className="flex flex-wrap items-center gap-1"
      >
        {REACTIONS.map((valeur) => {
          const choisie = mienne === valeur;
          return (
            <Bouton
              key={valeur}
              variante={choisie ? 'faible' : 'fantome'}
              taille="petite"
              aria-pressed={choisie}
              aria-label={NOM_DE_LA_REACTION[valeur]}
              disabled={enCours}
              onClick={() => onPoser(choisie ? null : valeur)}
            >
              <span aria-hidden="true">{valeur}</span>
            </Bouton>
          );
        })}
      </div>

      {desAutres.length > 0 && (
        <ul className="flex flex-wrap items-center gap-2">
          {desAutres.map((posee, rang) => (
            <li
              key={`${posee.valeur}-${String(rang)}`}
              className="text-13 text-texte-faible"
            >
              <span aria-hidden="true">{posee.valeur}</span>{' '}
              <span className="sr-only">{nomLisible(posee.valeur)} de </span>
              {/* « Le produit ne montre jamais d'un tiers plus que son PRÉNOM », et vide
                  sur un compte anonymisé : « ses mots restent, son identité part ». */}
              {nomAffiche(posee.de.prenom)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
